export const M5_UNIT_5 = {
  id: "m5-u5-could",
  title: "Unit 5: Past Abilities - Could & Couldn't",
  topic: "Talking about what you could and couldn't do in the past",
  iconType: "star",
  grammarImage: "/grammar/grammar-m5-u5.jpg",
  grammarText: "We use 'could' to talk about general abilities in the past, and 'couldn't' for things we weren't able to do. Could is the past form of can. Just like can/can't, could/couldn't stay the same for all subjects (I could, she could, they could). We often use 'when' to show the time: 'I could swim when I was 5.' Could doesn't always mean past—it can also show possibility or make polite requests, but in this unit we focus on past abilities. This helps you talk about your childhood skills, past limitations, and how your abilities have changed over time.",

  sets: [
    // ===================================================================
    // SET 1: CAN VS COULD RECOGNITION (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u5-s1",
      title: "Set 1: Now vs Then",
      questions: [
        {
          type: "match",
          q: "Match the time to the ability:",
          pairs: [
            { left: "Now I can drive", right: "Present ability" },
            { left: "When I was 10, I could ride a bike", right: "Past ability" },
            { left: "I can't fly", right: "Present impossibility" }
          ],
          a: "completed",
          successtip: "✓ CAN/CAN'T = now. COULD/COULDN'T = past. Simple pattern! 🚗🚲"
        },
        {
          type: "mc",
          q: "NOW: I can speak English. BEFORE: When I was 5, I ___ speak Spanish.",
          options: ["could", "can", "couldn't"],
          a: "could",
          successtip: "✓ COULD! Past ability! CAN (present) → COULD (past)! 🗣️"
        },
        {
          type: "input",
          q: "NOW: She can code. 10 YEARS AGO: She ___ even use a computer!",
          a: "couldn't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["could not"] },
          successtip: "✓ COULDN'T! No ability back then. Big change! 💻"
        },
        {
          type: "mc",
          q: "READ: 'When I was a kid, I could climb trees all day. Now I can barely walk up stairs!' What changed?",
          options: ["Had more energy before", "Has more energy now", "No change"],
          a: "Had more energy before",
          successtip: "✓ COULD (past) vs CAN (present)! Getting older! 🌳😅"
        },
        {
          type: "order",
          q: "Traduce: Cuando era niño, podía dormir 12 horas.",
          words: ["When", "I", "was", "a", "kid,", "I", "could", "sleep", "12", "hours"],
          a: "When I was a kid, I could sleep 12 hours",
          successtip: "✓ Ah, childhood! Now we're lucky to get 6! 😴"
        },
        {
          type: "input",
          q: "NOW: He can't speak French. BEFORE: He ___ speak it when he lived in Paris.",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ COULD! Lost the skill! 'Use it or lose it!' 🇫🇷"
        },
        {
          type: "match",
          q: "Compare abilities:",
          pairs: [
            { left: "I can swim (now)", right: "Current skill" },
            { left: "I could swim (before)", right: "Past skill" },
            { left: "I couldn't swim (before)", right: "Didn't have skill" }
          ],
          a: "completed",
          successtip: "✓ Three states: can/could/couldn't! 🏊‍♂️"
        },
        {
          type: "mc",
          q: "CONFUSING: Right now I ___ helping you, but yesterday I ___ because I was busy.",
          options: ["am / couldn't", "can / couldn't", "could / can't"],
          a: "am / couldn't",
          successtip: "✓ AM helping (present continuous NOW) vs COULDN'T (past)! ⏰"
        },
        {
          type: "input",
          q: "As a teenager, I ___ stay up all night. Now I fall asleep at 10pm!",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ COULD! Teenage energy vs adult reality! 🌙→😴"
        },
        {
          type: "mc",
          q: "READ: 'In college I could eat whatever I wanted and stay thin. Now I can't even look at a cookie without gaining weight!' This shows:",
          options: ["Metabolism changes with age", "Always had diet problems", "Never could eat freely"],
          a: "Metabolism changes with age",
          successtip: "✓ Classic story! COULD (college) vs CAN'T (now)! 🍕→🥗"
        }
      ]
    },

    // ===================================================================
    // SET 2: CHILDHOOD ABILITIES (10 questions) - A2
    // ===================================================================
    {
      id: "m5-u5-s2",
      title: "Set 2: When I Was Young...",
      questions: [
        {
          type: "match",
          q: "Match childhood abilities:",
          pairs: [
            { left: "I could read when I was 4", right: "Early reader" },
            { left: "She could play piano by 6", right: "Musical talent" },
            { left: "They could speak 2 languages", right: "Bilingual kids" }
          ],
          a: "completed",
          successtip: "✓ Kids learn fast! Could = past ability with 'when'! 👶📚"
        },
        {
          type: "mc",
          q: "When I was 5, I ___ ride a bike without training wheels.",
          options: ["could", "can", "couldn't"],
          a: "could",
          successtip: "✓ COULD! Proud childhood moment! 🚲✨"
        },
        {
          type: "input",
          q: "My brother ___ walk until he was 2 years old. He was a late bloomer!",
          a: "couldn't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["could not"] },
          successtip: "✓ COULDN'T! Everyone develops at their own pace! 👶"
        },
        {
          type: "order",
          q: "Traduce: Cuando tenía 8 años, podía nadar muy rápido.",
          words: ["When", "I", "was", "8,", "I", "could", "swim", "very", "fast"],
          a: "When I was 8, I could swim very fast",
          successtip: "✓ Childhood swimming champ! 🏊‍♂️🏆"
        },
        {
          type: "mc",
          q: "READ: 'My sister was a genius! She could solve math problems in her head when she was 7. I couldn't even count to 100!' What's the contrast?",
          options: ["Sister was better at math", "Both were good", "Author was better"],
          a: "Sister was better at math",
          successtip: "✓ Sibling comparison! COULD vs COULDN'T! Everyone has different talents! 🧮"
        },
        {
          type: "input",
          q: "In elementary school, I ___ remember everything I read. I had a photographic memory!",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ COULD! Amazing skill! Wish I still had it! 🧠✨"
        },
        {
          type: "match",
          q: "Age + abilities:",
          pairs: [
            { left: "At 3: couldn't tie shoes", right: "Too young" },
            { left: "At 10: could cook simple meals", right: "Growing independence" },
            { left: "At 16: could drive", right: "Teen milestone" }
          ],
          a: "completed",
          successtip: "✓ Skills develop with age! 👟→🍳→🚗"
        },
        {
          type: "input",
          q: "When I was little, I ___ whistle. It took me years to learn!",
          a: "couldn't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["could not"] },
          successtip: "✓ COULDN'T! Some skills take time! 🎶"
        },
        {
          type: "order",
          q: "Traduce: A los 12 años, podía tocar la guitarra bastante bien.",
          words: ["At", "12,", "I", "could", "play", "the", "guitar", "pretty", "well"],
          a: "At 12, I could play the guitar pretty well",
          successtip: "✓ Musical childhood! Pretty well = bastante bien! 🎸"
        },
        {
          type: "mc",
          q: "MINI-STORY: 'As a kid, I could climb anything—trees, walls, fences. My parents couldn't believe it! Now I can barely climb a ladder without getting dizzy.' What's the message?",
          options: ["Lost fearlessness with age", "Always scared of heights", "Still climbs everything"],
          a: "Lost fearlessness with age",
          successtip: "✓ Childhood bravery vs adult caution! COULD (past) vs CAN'T (now)! 🌳😰"
        }
      ]
    },

    // ===================================================================
    // SET 3: PAST ABILITIES WITH REASONS (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u5-s3",
      title: "Set 3: Could Because...",
      questions: [
        {
          type: "match",
          q: "Match the ability to the reason:",
          pairs: [
            { left: "I could stay up late", right: "Because I had no job" },
            { left: "She could travel freely", right: "Because she had no kids" },
            { left: "They could save money", right: "Because rent was cheap" }
          ],
          a: "completed",
          successtip: "✓ Could + because = ability + reason! Logic! 💡"
        },
        {
          type: "mc",
          q: "In college, I could party every night ___ I didn't have early classes.",
          options: ["because", "but", "when"],
          a: "because",
          successtip: "✓ BECAUSE = gives the reason! Freedom of college life! 🎉"
        },
        {
          type: "input",
          q: "When I lived alone, I ___ eat pizza for breakfast because nobody judged me!",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ COULD! Living alone = no rules! 🍕😋"
        },
        {
          type: "order",
          q: "Traduce: Podía trabajar desde casa porque mi jefe era flexible.",
          words: ["I", "could", "work", "from", "home", "because", "my", "boss", "was", "flexible"],
          a: "I could work from home because my boss was flexible",
          successtip: "✓ Good boss = more freedom! 🏠💼"
        },
        {
          type: "mc",
          q: "She couldn't come to the party ___ she was sick.",
          options: ["because", "but", "when"],
          a: "because",
          successtip: "✓ BECAUSE = explains why she couldn't! 🤒"
        },
        {
          type: "input",
          q: "Before smartphones, we ___ focus better because there were no distractions.",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ COULD! Pre-smartphone life! Now we can't focus for 5 minutes! 📱→📚"
        },
        {
          type: "match",
          q: "Negative + reason:",
          pairs: [
            { left: "Couldn't sleep", right: "Because of the noise" },
            { left: "Couldn't go out", right: "Because it was raining" },
            { left: "Couldn't eat", right: "Because I was full" }
          ],
          a: "completed",
          successtip: "✓ Couldn't + because = limitation + reason! ❌➡️💡"
        },
        {
          type: "input",
          q: "I couldn't afford that car ___ it was too expensive. (connecting word)",
          a: "because",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ BECAUSE! Dreams vs budget! 🚗💰"
        },
        {
          type: "order",
          q: "Traduce: No podíamos viajar porque no teníamos dinero.",
          words: ["We", "couldn't", "travel", "because", "we", "didn't", "have", "money"],
          a: "We couldn't travel because we didn't have money",
          successtip: "✓ Financial limitations! Common struggle! ✈️💸"
        },
        {
          type: "mc",
          q: "READ: 'In 2020, I could work in my pajamas all day because nobody saw me on Zoom. Now I can't—we have cameras on!' What changed?",
          options: ["Company policy about cameras", "Still works in pajamas", "Never worked in pajamas"],
          a: "Company policy about cameras",
          successtip: "✓ Pandemic freedom vs return to 'normal'! COULD (2020) vs CAN'T (now)! 👔📹"
        }
      ]
    },

    // ===================================================================
    // SET 4: TECHNOLOGY & MODERN LIFE (10 questions) - A2+
    // ===================================================================
    {
      id: "m5-u5-s4",
      title: "Set 4: How Times Have Changed",
      questions: [
        {
          type: "match",
          q: "Then vs now:",
          pairs: [
            { left: "Before: Could memorize phone numbers", right: "Now: Can't remember any" },
            { left: "Before: Could read maps", right: "Now: Use GPS for everything" },
            { left: "Before: Could wait patiently", right: "Now: Want instant everything" }
          ],
          a: "completed",
          successtip: "✓ Technology changed our abilities! COULD vs CAN! 📱🗺️"
        },
        {
          type: "mc",
          q: "In 2005, I ___ binge-watch entire series because streaming didn't exist yet!",
          options: ["couldn't", "could", "can't"],
          a: "couldn't",
          successtip: "✓ COULDN'T! Pre-Netflix life! Had to wait weekly for episodes! 📺⏰"
        },
        {
          type: "input",
          q: "Before Google, we ___ fact-check everything instantly. We had to use encyclopedias!",
          a: "couldn't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["could not"] },
          successtip: "✓ COULDN'T! Pre-internet research was HARD! 📚🔍"
        },
        {
          type: "order",
          q: "Traduce: Antes de las redes sociales, podíamos tener privacidad real.",
          words: ["Before", "social", "media,", "we", "could", "have", "real", "privacy"],
          a: "Before social media, we could have real privacy",
          successtip: "✓ Everything online now! Privacy = luxury! 🔒📱"
        },
        {
          type: "mc",
          q: "READ: 'When I was in college (2010), I could go an entire day without checking my phone. Now I can't go 10 minutes!' This shows:",
          options: ["Phone addiction increased", "Never used phones", "Uses phone less now"],
          a: "Phone addiction increased",
          successtip: "✓ Scary but true! COULD go all day → CAN'T go 10 minutes! 📱😬"
        },
        {
          type: "input",
          q: "In the '90s, you ___ download a song in 2 seconds. It took HOURS!",
          a: "couldn't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["could not"] },
          successtip: "✓ COULDN'T! Dial-up internet memories! Now it's instant! 🎵💿"
        },
        {
          type: "match",
          q: "Gaming evolution:",
          pairs: [
            { left: "1990s: Could play offline", right: "Single-player games" },
            { left: "2000s: Could play online", right: "Multiplayer revolution" },
            { left: "Now: Can stream gameplay", right: "Content creation era" }
          ],
          a: "completed",
          successtip: "✓ Gaming changed! COULD/CAN with different abilities! 🎮📡"
        },
        {
          type: "input",
          q: "Before smartphones, we ___ get lost easily because there was no GPS!",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "Getting lost = ability in this context (it was possible/common!)",
          successtip: "✓ COULD! Getting lost was normal! Now impossible with Google Maps! 🗺️"
        },
        {
          type: "order",
          q: "Traduce: Antes, podíamos pasar tiempo sin pantallas. Ahora no podemos.",
          words: ["Before,", "we", "could", "spend", "time", "without", "screens.", "Now", "we", "can't"],
          a: "Before, we could spend time without screens. Now we can't",
          successtip: "✓ Screen-free life = impossible now! COULD vs CAN'T! 📱💻📺"
        },
        {
          type: "mc",
          q: "NOSTALGIA: 'As kids, we could play outside until dark and our parents didn't worry. Now parents can't let kids do that anymore.' Why?",
          options: ["World seems less safe now", "Kids play outside more", "Nothing changed"],
          a: "World seems less safe now",
          successtip: "✓ Different times! COULD (past freedom) vs CAN'T (current caution)! 🏞️→🏠"
        }
      ]
    },

    // ===================================================================
    // SET 5: LIMITATIONS & OBSTACLES (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u5-s5",
      title: "Set 5: Couldn't Because...",
      questions: [
        {
          type: "match",
          q: "Match limitations to obstacles:",
          pairs: [
            { left: "Couldn't afford it", right: "Money problem" },
            { left: "Couldn't understand it", right: "Too difficult" },
            { left: "Couldn't find time", right: "Too busy" }
          ],
          a: "completed",
          successtip: "✓ Couldn't = past limitation + reason! 🚫"
        },
        {
          type: "mc",
          q: "I wanted to go to the concert, but I ___ get tickets—they sold out in minutes!",
          options: ["couldn't", "could", "can't"],
          a: "couldn't",
          successtip: "✓ COULDN'T! Frustrating situation! Happens with popular events! 🎫😭"
        },
        {
          type: "input",
          q: "She ___ finish the exam because she ran out of time.",
          a: "couldn't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["could not"] },
          successtip: "✓ COULDN'T! Exam stress! Time management is key! ⏰📝"
        },
        {
          type: "order",
          q: "Traduce: No pude dormir porque los vecinos hacían mucho ruido.",
          words: ["I", "couldn't", "sleep", "because", "the", "neighbors", "were", "too", "loud"],
          a: "I couldn't sleep because the neighbors were too loud",
          successtip: "✓ Noisy neighbors = universal problem! 😴🔊"
        },
        {
          type: "mc",
          q: "They wanted to travel, but they ___ because of the pandemic.",
          options: ["couldn't", "could", "can"],
          a: "couldn't",
          successtip: "✓ COULDN'T! 2020-2021 reality! Travel restrictions! ✈️🚫"
        },
        {
          type: "input",
          q: "I ___ see the screen because someone tall sat in front of me at the movies.",
          a: "couldn't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["could not"] },
          successtip: "✓ COULDN'T! Movie theater frustration! Why are tall people always in front?! 🎬🙈"
        },
        {
          type: "match",
          q: "Common obstacles:",
          pairs: [
            { left: "Couldn't afford", right: "Too expensive" },
            { left: "Couldn't reach", right: "Too far/high" },
            { left: "Couldn't hear", right: "Too noisy" }
          ],
          a: "completed",
          successtip: "✓ Physical and financial limitations! Real life! 💰📏🔊"
        },
        {
          type: "input",
          q: "We ___ make it to the wedding because our flight was canceled.",
          a: "couldn't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["could not"] },
          successtip: "✓ COULDN'T! Travel problems! So disappointing! ✈️❌💒"
        },
        {
          type: "order",
          q: "Traduce: No pudieron entrar porque olvidaron sus IDs.",
          words: ["They", "couldn't", "get", "in", "because", "they", "forgot", "their", "IDs"],
          a: "They couldn't get in because they forgot their IDs",
          successtip: "✓ Always happens at clubs/bars! Check your pockets! 🪪🚪"
        },
        {
          type: "mc",
          q: "READ: 'I applied for that job, but I couldn't get it. I didn't have enough experience. Five years later, I could finally get a similar position!' What changed?",
          options: ["Gained experience over time", "Same experience level", "Lost experience"],
          a: "Gained experience over time",
          successtip: "✓ Persistence pays off! COULDN'T (before) → COULD (after gaining experience)! 💼⏰"
        }
      ]
    },

    // ===================================================================
    // SET 6: POLITE REQUESTS (SUBTLE) (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u5-s6",
      title: "Set 6: Could You...?",
      questions: [
        {
          type: "match",
          q: "Match the request types:",
          pairs: [
            { left: "Can you help? (direct)", right: "Casual/informal" },
            { left: "Could you help? (polite)", right: "More polite" },
            { left: "Would you mind helping?", right: "Very polite" }
          ],
          a: "completed",
          tip: "Could = more polite than can for requests!",
          successtip: "✓ 'Could' sounds more polite than 'can'! Professional tip! 🎩"
        },
        {
          type: "mc",
          q: "At a restaurant: 'Excuse me, ___ I have some water?'",
          options: ["could", "can", "both work"],
          a: "both work",
          tip: "Both are fine, but 'could' sounds slightly more polite!",
          successtip: "✓ Both work! Could = bit more polite! 'Could I' vs 'Can I'! 🥤"
        },
        {
          type: "input",
          q: "To your boss: '___ you review this report when you have time?'",
          a: "Could",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Can", "Would"] },
          tip: "'Could' is more professional than 'can' with superiors!",
          successtip: "✓ COULD! Professional tone with boss! 💼"
        },
        {
          type: "order",
          q: "Traduce: ¿Podrías pasarme la sal, por favor?",
          words: ["Could", "you", "pass", "me", "the", "salt,", "please?"],
          a: "Could you pass me the salt, please?",
          tip: "'Could you' is more polite than 'Can you' at the table!",
          successtip: "✓ Table manners! Could = polite request! 🧂"
        },
        {
          type: "mc",
          q: "Which is MORE polite? A) 'Can you be quiet?' B) 'Could you please keep it down?'",
          options: ["B is more polite", "A is more polite", "Same politeness"],
          a: "B is more polite",
          successtip: "✓ B! 'Could' + 'please' + softer words = maximum politeness! 🤫"
        },
        {
          type: "input",
          q: "Formal email: '___ you send me the document by Friday?'",
          a: "Could",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["Would"] },
          successtip: "✓ COULD! Professional written communication! 📧"
        },
        {
          type: "match",
          q: "Context matters:",
          pairs: [
            { left: "To friend: Can you help?", right: "Casual" },
            { left: "To stranger: Could you help?", right: "Polite" },
            { left: "To customer: How may I help?", right: "Very formal" }
          ],
          a: "completed",
          successtip: "✓ Adjust politeness to relationship! Friends = can, Strangers = could! 🗣️"
        },
        {
          type: "input",
          q: "At a store: 'Excuse me, ___ I try this on?' (ask permission)",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["can"] },
          tip: "Both 'could I' and 'can I' work, could is slightly more polite!",
          successtip: "✓ COULD I! Asking permission politely! 👔"
        },
        {
          type: "order",
          q: "Traduce: ¿Podrías hablar más despacio, por favor?",
          words: ["Could", "you", "speak", "more", "slowly,", "please?"],
          a: "Could you speak more slowly, please?",
          successtip: "✓ Super useful phrase! Learning language = need slow speech! 🗣️🐌"
        },
        {
          type: "mc",
          q: "READ: 'My roommate kept playing loud music at 2am. I said \"Can you turn it down?\" but he ignored me. The next night I said \"Could you PLEASE keep it down? I have work tomorrow.\" He finally listened!' Why did the second approach work?",
          options: ["More polite = harder to ignore", "Said it louder", "Same approach"],
          a: "More polite = harder to ignore",
          successtip: "✓ Politeness strategy! 'Could' + 'please' = harder to refuse! Sometimes being nice works! 🎵🤫"
        }
      ]
    },

    // ===================================================================
    // SET 7: IDIOMS & EXPRESSIONS (PASSIVE) (10 questions) - B1
    // ===================================================================
    {
      id: "m5-u5-s7",
      title: "Set 7: Common Expressions",
      questions: [
        {
          type: "match",
          q: "READ: Match expressions to meanings:",
          pairs: [
            { left: "I could eat a horse", right: "Very hungry" },
            { left: "I couldn't care less", right: "Don't care at all" },
            { left: "I couldn't believe my eyes", right: "Very surprised" }
          ],
          a: "completed",
          tip: "These are idioms! 'Could eat a horse' = super hungry (not literal!)",
          successtip: "✓ English idioms with 'could'! Not literal meanings! 🐴😲"
        },
        {
          type: "mc",
          q: "READ: 'After the gym, I was starving! I could eat a horse!' What does this mean?",
          options: ["Extremely hungry", "Wants to eat horse meat", "Not hungry"],
          a: "Extremely hungry",
          tip: "Idiom! 'Could eat a horse' = VERY hungry (exaggeration!)",
          successtip: "✓ Idiom! Could eat a HORSE = super hungry! Not literal! 🍔🍕"
        },
        {
          type: "input",
          q: "READ: 'When I saw the price, I couldn't believe my ___!' (eyes/ears)",
          a: "eyes",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["ears"] },
          tip: "'Couldn't believe my eyes' = shocked by what you SAW. 'Ears' = shocked by what you HEARD!",
          successtip: "✓ Both work! Eyes (saw), Ears (heard)! Expression for shock! 😱"
        },
        {
          type: "mc",
          q: "READ: 'She said my outfit looked terrible. Honestly? I couldn't care less what she thinks!' What does this mean?",
          options: ["Doesn't care at all", "Cares a little", "Very upset"],
          a: "Doesn't care at all",
          tip: "'Couldn't care less' = zero care! Don't care AT ALL!",
          successtip: "✓ 'Couldn't care less' = I don't give a damn! Zero care! 😎"
        },
        {
          type: "order",
          q: "READ: Put the expression in order:",
          words: ["I", "could", "use", "a", "break"],
          a: "I could use a break",
          tip: "'Could use a break' = need a rest!",
          successtip: "✓ 'Could use X' = need/want something! 'I could use coffee' = I need coffee! ☕😴"
        },
        {
          type: "input",
          q: "READ: 'The party was so boring, I couldn't be ___.' (bothered/interested)",
          a: "bothered",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'Couldn't be bothered' = too lazy/unmotivated to do something!",
          successtip: "✓ 'Couldn't be bothered' = too lazy to care! British expression! 🇬🇧😴"
        },
        {
          type: "match",
          q: "READ: Expression meanings:",
          pairs: [
            { left: "Could use a drink", right: "Need/want a drink" },
            { left: "Couldn't tell you", right: "Don't know" },
            { left: "Could tell something was wrong", right: "Sensed a problem" }
          ],
          a: "completed",
          successtip: "✓ 'Could tell' = intuition/feeling! 'Could use' = need! Common phrases! 🍺🔮"
        },
        {
          type: "mc",
          q: "READ: 'When I walked in, everyone went quiet. I could tell something was wrong.' What does 'could tell' mean here?",
          options: ["Sensed/intuited", "Was told directly", "Guessed randomly"],
          a: "Sensed/intuited",
          tip: "'Could tell' = sense/feel something without being told directly!",
          successtip: "✓ 'Could tell' = intuition! You FEEL it without proof! 🔮"
        },
        {
          type: "input",
          q: "READ: 'After 8 hours of meetings, I ___ use a vacation!' (could/couldn't)",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "'Could use' = really need!",
          successtip: "✓ 'Could use a vacation' = desperately need one! We all feel this! 🏖️😭"
        },
        {
          type: "mc",
          q: "READ STORY: 'My friend said \"I'm fine\" but I could tell she was upset. Her voice was shaky and she couldn't look me in the eye.' What helped identify the problem?",
          options: ["Body language + intuition", "She said she was upset", "Nothing seemed wrong"],
          a: "Body language + intuition",
          successtip: "✓ 'Could tell' (intuition) + 'couldn't look' (body language)! Reading people! 👀"
        }
      ]
    },

    // ===================================================================
    // SET 8: SLANG & COLOQUIAL (PASSIVE) (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u5-s8",
      title: "Set 8: Casual Talk",
      questions: [
        {
          type: "match",
          q: "READ: Match slang to formal meaning:",
          pairs: [
            { left: "I could die! (embarrassed)", right: "Very embarrassed" },
            { left: "I couldn't even", right: "Too shocked to react" },
            { left: "I could never", right: "Would never do that" }
          ],
          a: "completed",
          tip: "Gen Z/Millennial slang! Exaggerations!",
          successtip: "✓ Young people slang! Dramatic exaggerations! 😱💀"
        },
        {
          type: "mc",
          q: "READ: 'OMG, when I tripped in front of my crush, I could die!' What does this mean?",
          options: ["Extremely embarrassed", "Literally dying", "Very happy"],
          a: "Extremely embarrassed",
          tip: "'Could die' = super embarrassed! (Not literal!)",
          successtip: "✓ 'Could die' = SO embarrassed! Dramatic teen/millennial expression! 😳💀"
        },
        {
          type: "input",
          q: "READ: 'When I saw the finale twist, I couldn't ___!' (react/even/speak)",
          a: "even",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["speak", "react"] },
          tip: "'Couldn't even' = too shocked/overwhelmed to finish thought! Gen Z phrase!",
          successtip: "✓ 'Couldn't even' = I CAN'T! Too shocked! Gen Z catchphrase! 😱"
        },
        {
          type: "mc",
          q: "READ: 'She eats bugs on her travel vlogs. I could never do that!' What does 'I could never' mean?",
          options: ["I would never do that", "I might try it", "I always do that"],
          a: "I would never do that",
          tip: "'I could never' = no way I'd do that! Strong refusal!",
          successtip: "✓ 'I could never' = that's not for me! Never ever! 🐛🙅"
        },
        {
          type: "order",
          q: "READ: Casual expression:",
          words: ["Could", "you", "not?"],
          a: "Could you not?",
          tip: "'Could you not?' = stop doing that! Annoyed but playful!",
          successtip: "✓ 'Could you NOT?' = stop it! Sarcastic/annoyed! Used with friends! 🙄"
        },
        {
          type: "input",
          q: "READ: 'My little brother kept singing loudly while I studied. Finally I said: Could you ___?' (not/stop/please)",
          a: "not",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["stop"] },
          tip: "'Could you not?' = annoyed request to stop! Short for 'Could you not do that?'",
          successtip: "✓ 'Could you NOT?' = annoying sibling situation! Universal! 😤🎵"
        },
        {
          type: "match",
          q: "READ: Slang contexts:",
          pairs: [
            { left: "I could die (social anxiety)", right: "Embarrassment" },
            { left: "I couldn't even (shock)", right: "Speechless" },
            { left: "Could you not? (annoyed)", right: "Stop that" }
          ],
          a: "completed",
          successtip: "✓ Context = everything! Same words, different situations! 😅"
        },
        {
          type: "mc",
          q: "READ: 'Friend: \"Want to go skydiving?\" Me: \"I could never! I'm terrified of heights!\"' The response shows:",
          options: ["Strong refusal due to fear", "Might consider it", "Already does it"],
          a: "Strong refusal due to fear",
          successtip: "✓ 'Could never' = absolute no! Too scary! 🪂😱"
        },
        {
          type: "input",
          q: "READ CHAT: 'A: I accidentally texted my boss a meme meant for you! B: OMG I could ___!' (die/laugh/cry)",
          a: "die",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["laugh", "cry"] },
          tip: "'Could die' = so embarrassed! Texting fails are the worst!",
          successtip: "✓ All work! Could DIE/LAUGH/CRY! Texting the wrong person = nightmare! 📱😱"
        },
        {
          type: "mc",
          q: "READ MEME: 'When someone spoils your favorite show: \"I literally couldn't even...\"' This expresses:",
          options: ["Too upset to finish the thought", "Completely fine", "Wants more spoilers"],
          a: "Too upset to finish the thought",
          successtip: "✓ 'Couldn't even...' (trails off) = TOO upset to complete sentence! Internet culture! 📺💔"
        }
      ]
    },

    // ===================================================================
    // SET 9: STORYTELLING COMPLEX (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u5-s9",
      title: "Set 9: Life Stories",
      questions: [
        {
          type: "match",
          q: "Match life changes:",
          pairs: [
            { left: "Couldn't afford college → Got scholarship", right: "Financial breakthrough" },
            { left: "Could barely walk → Ran marathon", right: "Health transformation" },
            { left: "Couldn't speak English → Got job in US", right: "Language success" }
          ],
          a: "completed",
          successtip: "✓ Transformation stories! Couldn't → Could! 🌟"
        },
        {
          type: "mc",
          q: "STORY: 'Five years ago, I couldn't run for 5 minutes without getting tired. Yesterday, I finished my first marathon!' This shows:",
          options: ["Major fitness transformation", "Always fit", "Got less fit"],
          a: "Major fitness transformation",
          successtip: "✓ Couldn't run 5 min → Ran marathon! Inspiring! 🏃‍♂️🏆"
        },
        {
          type: "input",
          q: "STORY: 'When I moved to Japan, I ___ order food because I didn't speak Japanese. Now I'm fluent!'",
          a: "couldn't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["could not"] },
          successtip: "✓ COULDN'T! Language barrier → Fluency! Immersion works! 🇯🇵🗣️"
        },
        {
          type: "order",
          q: "Traduce: No podía imaginar vivir en otro país, pero ahora no puedo imaginar regresar.",
          words: ["I", "couldn't", "imagine", "living", "abroad,", "but", "now", "I", "can't", "imagine", "going", "back"],
          a: "I couldn't imagine living abroad, but now I can't imagine going back",
          successtip: "✓ Full circle! Couldn't imagine leaving → Can't imagine returning! Expat life! ✈️🏠"
        },
        {
          type: "mc",
          q: "STORY: 'My grandma couldn't use a smartphone at 75. At 80, she's posting on Instagram daily!' What changed?",
          options: ["Learned with patience", "Always knew tech", "Still can't use it"],
          a: "Learned with patience",
          successtip: "✓ Age = just a number! Couldn't → Can! Never too late to learn! 👵📱"
        },
        {
          type: "input",
          q: "REFLECTION: 'Looking back, I ___ see how toxic that relationship was. Now I can recognize red flags immediately.'",
          a: "couldn't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["could not"] },
          successtip: "✓ COULDN'T! Hindsight is 20/20! Growth = seeing patterns! 🚩💔"
        },
        {
          type: "match",
          q: "Before vs after:",
          pairs: [
            { left: "Couldn't code", right: "Now: Software engineer" },
            { left: "Couldn't cook", right: "Now: Chef at restaurant" },
            { left: "Couldn't save money", right: "Now: Bought a house" }
          ],
          a: "completed",
          successtip: "✓ Skills develop! Couldn't → career/success! 💻👨‍🍳🏠"
        },
        {
          type: "input",
          q: "NOSTALGIA: 'In my 20s, I ___ afford avocado toast AND rent. Now I own a house but still can't afford both!'",
          a: "couldn't",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["could not"] },
          tip: "Reference to millennial 'avocado toast' economy joke!",
          successtip: "✓ COULDN'T! Millennial economy joke! Avocado toast = symbol of lifestyle vs housing prices! 🥑🏠😂"
        },
        {
          type: "order",
          q: "Traduce: Antes no podía hablar en público sin temblar, ahora doy presentaciones semanales.",
          words: ["Before", "I", "couldn't", "public", "speak", "without", "shaking,", "now", "I", "give", "presentations", "weekly"],
          a: "Before I couldn't public speak without shaking, now I give presentations weekly",
          successtip: "✓ Conquered fear! Couldn't → professional skill! Public speaking = top fear! 🎤"
        },
        {
          type: "mc",
          q: "COMPLEX STORY: 'I couldn't get into my dream school, so I went to community college. There, I could afford to experiment with different majors. I found my passion! Now I couldn't be happier with how things turned out.' What's the message?",
          options: ["Rejection led to better path", "Always goes as planned", "Still unhappy"],
          a: "Rejection led to better path",
          successtip: "✓ Beautiful! Couldn't (rejection) → Could (freedom) → Couldn't be happier! Life works out! 🎓✨"
        }
      ]
    },

    // ===================================================================
    // SET 10: MIXED MASTERY (10 questions) - B1+
    // ===================================================================
    {
      id: "m5-u5-s10",
      title: "Set 10: Could Master Class",
      questions: [
        {
          type: "match",
          q: "All meanings of 'could':",
          pairs: [
            { left: "I could swim (when I was 5)", right: "Past ability" },
            { left: "Could you help me?", right: "Polite request" },
            { left: "It could rain tomorrow", right: "Possibility (future)" }
          ],
          a: "completed",
          tip: "'Could' has 3 main uses: past ability, polite request, possibility!",
          successtip: "✓ One word, three uses! Past ability/Request/Possibility! 🏊‍♂️🙏☁️"
        },
        {
          type: "mc",
          q: "CONTEXT: 'Could you turn down the music?' This 'could' is:",
          options: ["Polite request", "Past ability", "Possibility"],
          a: "Polite request",
          successtip: "✓ Polite REQUEST! Not about past or future! 🎵🤫"
        },
        {
          type: "input",
          q: "CONTEXT: 'When I was younger, I ___ eat anything without gaining weight.' (could/can)",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true },
          successtip: "✓ COULD! Past ability! Ah, youth! Now = different story! 🍕➡️🏋️"
        },
        {
          type: "order",
          q: "Traduce: Podría llover mañana, así que lleva paraguas.",
          words: ["It", "could", "rain", "tomorrow,", "so", "bring", "an", "umbrella"],
          a: "It could rain tomorrow, so bring an umbrella",
          tip: "This 'could' = possibility/maybe (not past ability!)",
          successtip: "✓ POSSIBILITY! Could = maybe! Not past ability here! ☔"
        },
        {
          type: "mc",
          q: "TRICKY: 'I could help you tomorrow.' Is this: A) Past ability B) Offering help (present/future)",
          options: ["B - Offering help", "A - Past ability", "Both"],
          a: "B - Offering help",
          tip: "'Tomorrow' = future! This 'could' = offering/possibility, not past!",
          successtip: "✓ B! 'Tomorrow' = future offer! Not past ability! Context matters! 🤝"
        },
        {
          type: "input",
          q: "COMPLEX: 'I ___ go to the party (past), but I ___ go because I was sick.' (could/couldn't)",
          a: "could",
          accept: { ignoreCase: true, ignorePunctuation: true },
          tip: "First blank = ability existed (could). Second = didn't go (couldn't)!",
          successtip: "✓ COULD...but COULDN'T! Had ability but obstacle prevented it! 🎉🤒"
        },
        {
          type: "match",
          q: "Identify the 'could' type:",
          pairs: [
            { left: "Could I borrow your phone?", right: "Request permission" },
            { left: "She could play piano at 6", right: "Past ability" },
            { left: "We could visit Paris next year", right: "Future possibility" }
          ],
          a: "completed",
          successtip: "✓ Same word, different meanings! Master all three! 📱🎹🗼"
        },
        {
          type: "input",
          q: "READING: 'I couldn't believe it! After years of trying, I could finally do a handstand!' Second 'could' =",
          a: "past ability",
          accept: { ignoreCase: true, ignorePunctuation: true, alts: ["past", "ability"] },
          successtip: "✓ PAST ABILITY! Achievement unlocked! 🤸‍♂️✨"
        },
        {
          type: "order",
          q: "Traduce: ¿Podrías revisar mi ensayo cuando tengas tiempo?",
          words: ["Could", "you", "review", "my", "essay", "when", "you", "have", "time?"],
          a: "Could you review my essay when you have time?",
          tip: "Polite request with 'could you'!",
          successtip: "✓ Polite request! Professional tone! 📝"
        },
        {
          type: "mc",
          q: "FINAL CHALLENGE: 'Ten years ago, I couldn't speak English. Today, I could help you with your homework, if you want. But tomorrow I couldn't because I'll be busy.' How many meanings of could/couldn't?",
          options: ["3 different uses", "All same meaning", "2 different uses"],
          a: "3 different uses",
          tip: "1) Past ability (couldn't speak), 2) Offer (could help), 3) Future impossibility (couldn't tomorrow)!",
          successtip: "✓ 🎉 PERFECTO! Three meanings: Past ability/Present offer/Future limitation! You're a COULD master! 🏆🌟"
        }
      ]
    }
  ],

 // ===================================================================
// READING SET: My Gaming Evolution
// ===================================================================
readingSet: {
  id: "m5-u5-reading",
  title: "Reading: My Gaming Evolution",
  category: "reading",

  questions: [
    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 1: Childhood Struggles (preguntas 1-3)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Early Years**

When I was 10, I couldn't beat any level in Super Mario. My older brother could finish the entire game in one sitting! I was so jealous. I practiced every day after school. By age 12, I could finally beat him in every game we played.`
    },
    {
      type: "mc",
      q: "At age 10, what was the problem?",
      options: ["Couldn't beat any level", "Was better than brother", "Didn't like gaming"],
      a: "Couldn't beat any level",
      successtip: "✓ COULDN'T beat levels! Starting point! 🎮"
    },
    {
      type: "input",
      q: "Who was better at games initially? His older ___.",
      a: "brother",
      accept: { ignoreCase: true, ignorePunctuation: true },
      successtip: "✓ BROTHER! Sibling rivalry! 👾"
    },
    {
      type: "mc",
      q: "What happened by age 12?",
      options: ["Could beat his brother", "Still couldn't win", "Quit gaming"],
      a: "Could beat his brother",
      successtip: "✓ COULD beat him! Practice paid off! Couldn't → Could! 🏆"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: High School Dedication (preguntas 4-6)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Competitive Discovery**

In high school, I discovered competitive gaming. I could spend 8 hours a day playing without getting bored. My parents couldn't understand it—they thought I was wasting time. But I couldn't care less what they thought. I was having fun and improving my skills.`
    },
    {
      type: "mc",
      q: "How much could he play daily during high school?",
      options: ["8 hours", "2 hours", "All day"],
      a: "8 hours",
      successtip: "✓ 8 HOURS! Dedication! ⏰🎮"
    },
    {
      type: "input",
      q: "What does 'couldn't care less' mean in this context?",
      a: "didn't care",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["did not care", "didn't care at all", "zero care"] },
      tip: "'Couldn't care less' = idiom for not caring at all!",
      successtip: "✓ Idiom! Couldn't care less = didn't care AT ALL! 😎"
    },
    {
      type: "mc",
      q: "How did his parents feel about his gaming?",
      options: ["Couldn't understand it", "Loved it", "Joined him"],
      a: "Couldn't understand it",
      successtip: "✓ Parents COULDN'T understand! Generation gap! 👨‍👩‍👦🎮"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Professional Success (preguntas 7-9)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Esports Career**

Now I'm 25 and a professional esports player. I couldn't believe it when I got my first sponsorship! Five years ago, I couldn't imagine making money from gaming. My parents still can't believe it's a real job, but I'm earning more than they ever could at my age. Sometimes I think: I couldn't have asked for a better career!`
    },
    {
      type: "mc",
      q: "What shocked him when his career started?",
      options: ["Getting sponsored", "Losing games", "Quitting gaming"],
      a: "Getting sponsored",
      successtip: "✓ First sponsorship! Couldn't believe it! Dream come true! 💰"
    },
    {
      type: "input",
      q: "Five years ago, what couldn't he imagine?",
      a: "making money from gaming",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["earning from gaming", "gaming as job", "professional gaming"] },
      successtip: "✓ Couldn't imagine it! Now it's reality! 🎮💵"
    },
    {
      type: "mc",
      q: "How do his parents feel about his job now?",
      options: ["Can't believe it's real", "Fully support it", "Make him quit"],
      a: "Can't believe it's real",
      successtip: "✓ Parents CAN'T believe it! But he's successful! 👨‍👩‍👦💼"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Conclusion (pregunta 10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Final Reflection**

Looking back at my gaming evolution, I realize that what started as a difficult hobby became my life's work. It took a lot of patience and many hours of practice to reach this level.`
    },
    {
      type: "mc",
      q: "COMPREHENSION: The story shows:",
      options: ["Passion + practice = unexpected career", "Gaming is waste of time", "Should listen to parents"],
      a: "Passion + practice = unexpected career",
      successtip: "✓ Perfect! Couldn't (struggles) → Could (skills) → Couldn't believe (success)! Follow your passion! 🌟🎮"
    }
  ]
},
  // ===================================================================
  // REVIEW SET (10 questions)
  // ===================================================================
  reviewSet: {
    id: "m5-u5-review",
    title: "Unit 5 Review: Could/Couldn't Mastery",
    questions: [
      {
        type: "match",
        q: "Review: Match times to forms:",
        pairs: [
          { left: "Present: I can", right: "Now" },
          { left: "Past: I could", right: "Before" },
          { left: "Past negative: I couldn't", right: "Wasn't able to" }
        ],
        a: "completed",
        successtip: "✓ Can/Could/Couldn't! Time forms! ⏰"
      },
      {
        type: "mc",
        q: "When I was 5, I ___ tie my shoes.",
        options: ["couldn't", "can't", "could"],
        a: "couldn't",
        successtip: "✓ COULDN'T! Past limitation! 👟"
      },
      {
        type: "input",
        q: "She ___ speak French when she lived in Paris.",
        a: "could",
        accept: { ignoreCase: true, ignorePunctuation: true },
        successtip: "✓ COULD! Past ability with 'when'! 🇫🇷"
      },
      {
        type: "order",
        q: "Make a sentence:",
        words: ["I", "couldn't", "sleep", "because", "of", "the", "noise"],
        a: "I couldn't sleep because of the noise",
        successtip: "✓ Couldn't + reason! 🔊😴"
      },
      {
        type: "mc",
        q: "Which is MORE polite? A) 'Can you help?' B) 'Could you help?'",
        options: ["B is more polite", "A is more polite", "Same"],
        a: "B is more polite",
        successtip: "✓ COULD = more polite! Professional tip! 🎩"
      },
      {
        type: "input",
        q: "IDIOM: 'After the workout, I was starving! I could eat a ___.' (animal)",
        a: "horse",
        accept: { ignoreCase: true, ignorePunctuation: true },
        tip: "Idiom: 'Could eat a horse' = super hungry!",
        successtip: "✓ HORSE! Idiom for extreme hunger! 🐴🍔"
      },
      {
        type: "mc",
        q: "EXPRESSION: 'I couldn't care less' means:",
        options: ["Don't care at all", "Care a lot", "Care a little"],
        a: "Don't care at all",
        successtip: "✓ Zero care! Common expression! 😎"
      },
      {
        type: "match",
        q: "Three uses of 'could':",
        pairs: [
          { left: "Past ability", right: "I could swim at 5" },
          { left: "Polite request", right: "Could you help?" },
          { left: "Possibility", right: "It could rain" }
        ],
        a: "completed",
        successtip: "✓ One word, three meanings! 🏊‍♂️🙏☔"
      },
      {
        type: "input",
        q: "NOW: I can drive. BEFORE: I ___ drive when I was 16.",
        a: "could",
        accept: { ignoreCase: true, ignorePunctuation: true },
        successtip: "✓ COULD! Past ability! 🚗"
      },
      {
        type: "mc",
        q: "FINAL: 'Ten years ago I couldn't speak English. Now I can teach it. I couldn't have imagined this!' This shows:",
        options: ["Couldn't (past) → Can (present) → Success beyond imagination", "No change", "Always could speak"],
        a: "Couldn't (past) → Can (present) → Success beyond imagination",
        successtip: "✓ 🎉 PERFECTO! Full transformation story! You mastered could/couldn't! 🏆🌟"
      }
    ]
  }
};
